import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, CreditCard, LogOut, Mail, Loader2, Settings } from "lucide-react";
import { toast } from "sonner";
import { isDespiaNative } from "@/lib/isDespiaNative";

export default function SubscriptionExpiredPage() {
  const { t, i18n } = useTranslation();
  const { company, signOut } = useAuth();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("billing-create-portal-session", {
        body: { return_url: window.location.origin }
      });
      if (error) {
        toast.error(t("subscriptionExpired.portalError"));
        return;
      }
      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        toast.error(t("subscriptionExpired.portalError"));
      }
    } catch {
      toast.error(t("subscriptionExpired.portalError"));
    } finally {
      setPortalLoading(false);
    }
  };

  const handleRenewSubscription = async () => {
    setLoading(true);
    try {
      // Use Stripe Checkout instead of Customer Portal for renewals
      // El checkout de Stripe sale en el idioma de la app, no en el del navegador.
      const { data, error } = await supabase.functions.invoke("create-renewal-session", {
        body: { locale: i18n.language },
      });
      
      if (error) {
        console.error("Renewal session error:", error);
        
        // Extract error message from Supabase response
        let errorMessage = "";
        try {
          if (error.context && typeof error.context.json === 'function') {
            const errorData = await error.context.json();
            errorMessage = errorData?.error || "";
          } else {
            errorMessage = error.message || "";
          }
        } catch {
          errorMessage = error.message || "";
        }
        
        // Check if it's a plan unavailable error
        if (errorMessage.includes("No price found") || 
            errorMessage.includes("price") || 
            errorMessage.includes("No such price")) {
          toast.error(t("subscriptionExpired.planUnavailable"));
        } else {
          toast.error(t("subscriptionExpired.portalError"));
        }
        return;
      }
      
      if (data?.url) {
        window.open(data.url, "_blank");
        toast.success(t("subscriptionExpired.portalOpening"));
      } else {
        toast.error(t("subscriptionExpired.portalError"));
      }
    } catch (err) {
      console.error("Error creating renewal session:", err);
      toast.error(t("subscriptionExpired.portalError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-destructive/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <Card className="relative z-10 w-full max-w-md border-destructive/20 bg-card/95 backdrop-blur-sm shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            {t("subscriptionExpired.title")}
          </CardTitle>
          <CardDescription className="text-muted-foreground mt-2">
            {t("subscriptionExpired.subtitle")}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Company info */}
          {company && (
            <div className="rounded-lg bg-muted/50 p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("subscriptionExpired.companyLabel")}:</span>
                <span className="font-medium text-foreground">{company.name}</span>
              </div>
              {company.subscription_tier && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t("subscriptionExpired.planLabel")}:</span>
                  <span className="font-medium text-foreground capitalize">{company.subscription_tier}</span>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <p className="text-sm text-muted-foreground text-center">
            {t("subscriptionExpired.description")}
          </p>

          {/* Actions */}
          <div className="space-y-3">
            {!isDespiaNative() && (
              <>
                <Button
                  onClick={handleRenewSubscription}
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary/90"
                  size="lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("subscriptionExpired.renewingButton")}
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2 h-4 w-4" />
                      {t("subscriptionExpired.renewButton")}
                    </>
                  )}
                </Button>

                <Button
                  onClick={handleOpenPortal}
                  disabled={portalLoading}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  {portalLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("subscriptionExpired.renewingButton")}
                    </>
                  ) : (
                    <>
                      <Settings className="mr-2 h-4 w-4" />
                      {t("subscriptionExpired.managePaymentButton", "Manage Payment Methods")}
                    </>
                  )}
                </Button>
              </>
            )}

            <Button
              onClick={signOut}
              variant="destructive"
              className="w-full"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t("subscriptionExpired.signOutButton")}
            </Button>
          </div>

          {/* Contact support */}
          <div className="pt-4 border-t border-border">
            <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-1">
              <Mail className="h-3 w-3" />
              {t("subscriptionExpired.contactSupport")}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
